import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Receipt, Calendar, CreditCard, CheckCircle, Clock,
  Download, ChevronDown, ChevronUp, DollarSign, RefreshCw
} from "lucide-react";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PurchaseHistory({ token, isDarkMode }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (token) {
      fetchTransactions();
    }
  }, [token]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/subscription/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error("Failed to fetch purchase history:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const formatAmount = (amount) => {
    if (typeof amount !== "number") return "$0.00";
    return `$${amount.toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "active":
        return "text-green-400 bg-green-500/20";
      case "pending":
        return "text-yellow-400 bg-yellow-500/20";
      case "cancelled":
      case "failed":
        return "text-red-400 bg-red-500/20";
      case "refunded":
        return "text-blue-400 bg-blue-500/20";
      default:
        return "text-gray-400 bg-gray-500/20";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "active":
        return <CheckCircle className="w-4 h-4" />;
      case "pending":
        return <Clock className="w-4 h-4" />;
      default:
        return <Receipt className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-12 bg-white/10 rounded-xl"></div>
        <div className="h-24 bg-white/10 rounded-xl"></div>
      </div>
    );
  }

  const displayedTransactions = expanded ? transactions : transactions.slice(0, 3);

  return (
    <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Receipt className="w-5 h-5 text-[#00f2ea]" />
          <h3 className="font-bold">Purchase History</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchTransactions}
          className="text-white/60"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Transactions List */}
      {transactions.length === 0 ? (
        <div className="text-center py-8">
          <Receipt className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm opacity-60">No purchase history yet</p>
          <p className="text-xs opacity-40 mt-1">Your transactions will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayedTransactions.map((transaction, idx) => (
            <motion.div
              key={transaction.id || idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-white/10' : 'bg-black/10'}`}>
                    <CreditCard className="w-5 h-5 text-[#00f2ea]" />
                  </div>
                  <div>
                    <p className="font-medium">
                      {transaction.description || transaction.plan || "Premium Subscription"}
                    </p>
                    <div className="flex items-center gap-2 text-xs opacity-50 mt-1">
                      <Calendar className="w-3 h-3" />
                      <span>{formatDate(transaction.date || transaction.created_at)}</span>
                    </div>
                    {transaction.payment_method && (
                      <p className="text-xs opacity-40 mt-1">
                        via {transaction.payment_method}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="font-bold text-lg">
                    {formatAmount(transaction.amount)}
                  </p>
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${getStatusColor(transaction.status)}`}>
                    {getStatusIcon(transaction.status)}
                    {transaction.status || "Completed"}
                  </span>
                </div>
              </div>

              {/* Additional Details */}
              {(transaction.subscription_period || transaction.invoice_id) && (
                <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-black/10'}`}>
                  <div className="flex flex-wrap gap-4 text-xs opacity-60">
                    {transaction.subscription_period && (
                      <span>Period: {transaction.subscription_period}</span>
                    )}
                    {transaction.invoice_id && (
                      <span>Invoice: {transaction.invoice_id}</span>
                    )}
                    {transaction.paypal_subscription_id && (
                      <span>PayPal ID: {transaction.paypal_subscription_id.slice(0, 12)}...</span>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          ))}

          {/* Show More/Less */}
          {transactions.length > 3 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="w-full mt-2"
            >
              {expanded ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-2" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-2" />
                  Show All ({transactions.length} transactions)
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Summary */}
      {transactions.length > 0 && (
        <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-white/10' : 'border-black/10'}`}>
          <div className="flex items-center justify-between text-sm">
            <span className="opacity-60">Total Spent</span>
            <span className="font-bold text-[#00f2ea]">
              {formatAmount(transactions.reduce((sum, t) => sum + (t.amount || 0), 0))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
